import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';

import {
  getAllOrdersAsAdmin,
} from '../../api/orderApi';


// =============================================================
// CONFIGURATION
// =============================================================

const SHIPPING_COST_DEFAULT = 5.99;


// =============================================================
// HELPERS
// =============================================================

const normalizeValue = (value) => {
  if (
    value === undefined ||
    value === null
  ) {
    return '';
  }

  return String(value)
    .trim()
    .toLowerCase();
};


// =============================================================
// ORDER STATUS
// =============================================================

const getNormalizedOrderStatus = (order) => {
  if (!order) {
    return '';
  }

  const status =
    order.status ??
    order.orderStatus ??
    '';

  return normalizeValue(status);
};


// =============================================================
// ORDER STATUS BADGE
// =============================================================

const getStatusBadge = (status) => {
  const normalizedStatus =
    normalizeValue(status);

  console.log(
    '📌 ORDER STATUS:',
    status,
    '| NORMALIZED:',
    normalizedStatus
  );

  const statusMap = {
    pending: 'warning',
    processing: 'info',
    completed: 'success',
    shipped: 'primary',
    delivered: 'success',
    cancelled: 'danger',
    canceled: 'danger',
    failed: 'danger',
    refunded: 'secondary',
  };

  const displayMap = {
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    failed: 'Failed',
    refunded: 'Refunded',
  };

  if (!normalizedStatus) {
    return (
      <Badge
        bg="secondary"
        className="px-2 py-1"
      >
        Unknown
      </Badge>
    );
  }

  return (
    <Badge
      bg={
        statusMap[normalizedStatus] ||
        'secondary'
      }
      className="px-2 py-1"
    >
      {displayMap[normalizedStatus] ||
        normalizedStatus}
    </Badge>
  );
};


// =============================================================
// PAYMENT STATUS
// =============================================================

const getPaymentStatus = (order) => {
  if (!order) {
    return 'pending';
  }

  // -----------------------------------------------------------
  // PRIMARY SOURCE
  // MongoDB paymentStatus
  // -----------------------------------------------------------

  if (
    order.paymentStatus !== undefined &&
    order.paymentStatus !== null &&
    String(order.paymentStatus).trim() !== ''
  ) {
    return normalizeValue(
      order.paymentStatus
    );
  }

  // -----------------------------------------------------------
  // FALLBACK
  // -----------------------------------------------------------

  if (order.isPaid === true) {
    return 'paid';
  }

  if (order.isPaid === false) {
    return 'unpaid';
  }

  // -----------------------------------------------------------
  // STRIPE PAYMENT INTENT
  // -----------------------------------------------------------

  if (
    order.paymentIntent ||
    order.paymentIntentId ||
    order.stripePaymentIntentId
  ) {
    return 'paid';
  }

  // -----------------------------------------------------------
  // COMPLETED ORDER
  // -----------------------------------------------------------

  if (
    normalizeValue(order.status) ===
    'completed'
  ) {
    return 'paid';
  }

  return 'pending';
};


// =============================================================
// PAYMENT BADGE
// =============================================================

const getPaymentBadge = (order) => {
  const status =
    getPaymentStatus(order);

  console.log(
    '💳 PAYMENT STATUS:',
    order?.paymentStatus,
    '| DISPLAY:',
    status
  );

  const badgeMap = {
    paid: 'success',
    completed: 'success',
    pending: 'warning',
    unpaid: 'secondary',
    processing: 'info',
    failed: 'danger',
    cancelled: 'danger',
    canceled: 'danger',
    refunded: 'dark',
  };

  const displayMap = {
    paid: 'Paid',
    completed: 'Paid',
    pending: 'Pending',
    unpaid: 'Unpaid',
    processing: 'Processing',
    failed: 'Failed',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    refunded: 'Refunded',
  };

  return (
    <Badge
      bg={
        badgeMap[status] ||
        'secondary'
      }
      className="px-2 py-1"
    >
      {displayMap[status] ||
        status ||
        'Unknown'}
    </Badge>
  );
};


// =============================================================
// PAYMENT METHOD
// =============================================================

const getPaymentMethod = (order) => {
  if (!order) {
    return 'Unknown';
  }

  const method =
    order.paymentMethod;

  if (!method) {
    return 'Unknown';
  }

  const normalized =
    normalizeValue(method);

  if (normalized === 'stripe') {
    return 'Stripe';
  }

  if (
    normalized === 'card' ||
    normalized === 'credit_card' ||
    normalized === 'creditcard'
  ) {
    return 'Card';
  }

  if (
    normalized === 'paypal'
  ) {
    return 'PayPal';
  }

  if (
    normalized === 'cash'
  ) {
    return 'Cash';
  }

  return String(method);
};


// =============================================================
// DATE FORMATTER
// =============================================================

const formatDate = (date) => {
  if (!date) {
    return '—';
  }

  try {
    const parsedDate =
      new Date(date);

    if (
      Number.isNaN(
        parsedDate.getTime()
      )
    ) {
      return '—';
    }

    return parsedDate.toLocaleString(
      'en-GB',
      {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }
    );
  } catch (error) {
    return '—';
  }
};


// =============================================================
// MONEY FORMATTER
// =============================================================

const formatMoney = (value) => {
  const number =
    Number(value);

  if (
    Number.isNaN(number)
  ) {
    return '£0.00';
  }

  return number.toLocaleString(
    'en-GB',
    {
      style: 'currency',
      currency: 'GBP',
    }
  );
};


// =============================================================
// ORDER ID
// =============================================================

const getOrderId = (order) => {
  if (!order) {
    return '—';
  }

  return (
    order._id ||
    order.id ||
    '—'
  );
};


// =============================================================
// CUSTOMER NAME
// =============================================================

const getCustomerName = (order) => {
  if (!order) {
    return 'Guest';
  }

  if (
    order.userId &&
    typeof order.userId === 'object'
  ) {
    return (
      order.userId.name ||
      order.userId.email ||
      'Customer'
    );
  }

  return (
    order.customerName ||
    order.name ||
    'Customer'
  );
};


// =============================================================
// CUSTOMER EMAIL
// =============================================================

const getCustomerEmail = (order) => {
  if (!order) {
    return '';
  }

  if (
    order.userId &&
    typeof order.userId === 'object'
  ) {
    return (
      order.userId.email ||
      ''
    );
  }

  return (
    order.customerEmail ||
    order.email ||
    ''
  );
};


// =============================================================
// PRODUCTS
// =============================================================

const getOrderProducts = (order) => {
  if (!order) {
    return [];
  }

  if (
    Array.isArray(order.products)
  ) {
    return order.products;
  }

  if (
    Array.isArray(order.items)
  ) {
    return order.items;
  }

  if (
    Array.isArray(order.orderItems)
  ) {
    return order.orderItems;
  }

  return [];
};


// =============================================================
// PRODUCT NAME
// =============================================================

const getProductName = (product) => {
  if (!product) {
    return 'Product';
  }

  return (
    product.name ||
    product.title ||
    product.productName ||
    'Product'
  );
};


// =============================================================
// PRODUCT PRICE
// =============================================================

const getProductPrice = (product) => {
  if (!product) {
    return 0;
  }

  return Number(
    product.price ??
    product.unitPrice ??
    0
  );
};


// =============================================================
// PRODUCT QUANTITY
// =============================================================

const getProductQuantity = (product) => {
  if (!product) {
    return 1;
  }

  return Number(
    product.quantity ??
    product.qty ??
    1
  );
};


// =============================================================
// SUBTOTAL
// =============================================================

const getSubtotal = (order) => {
  if (!order) {
    return 0;
  }

  if (
    order.subtotal !== undefined &&
    order.subtotal !== null
  ) {
    return Number(
      order.subtotal
    );
  }

  const products =
    getOrderProducts(order);

  return products.reduce(
    (total, product) => {
      return (
        total +
        getProductPrice(
          product
        ) *
          getProductQuantity(
            product
          )
      );
    },
    0
  );
};


// =============================================================
// SHIPPING
// =============================================================

const getShipping = (order) => {
  if (!order) {
    return 0;
  }

  if (
    order.shipping !== undefined &&
    order.shipping !== null
  ) {
    return Number(
      order.shipping
    );
  }

  if (
    order.shippingCost !== undefined &&
    order.shippingCost !== null
  ) {
    return Number(
      order.shippingCost
    );
  }

  if (
    order.deliveryCost !== undefined &&
    order.deliveryCost !== null
  ) {
    return Number(
      order.deliveryCost
    );
  }

  return SHIPPING_COST_DEFAULT;
};


// =============================================================
// TAX
// =============================================================

const getTax = (order) => {
  if (!order) {
    return 0;
  }

  return Number(
    order.tax ??
    order.taxAmount ??
    0
  );
};


// =============================================================
// TOTAL
// =============================================================

const getTotal = (order) => {
  if (!order) {
    return 0;
  }

  if (
    order.total !== undefined &&
    order.total !== null
  ) {
    return Number(
      order.total
    );
  }

  return (
    getSubtotal(order) +
    getShipping(order) +
    getTax(order)
  );
};


// =============================================================
// ADDRESS
// =============================================================

const getShippingAddress = (order) => {
  if (!order) {
    return null;
  }

  return (
    order.shippingAddress ||
    order.address ||
    null
  );
};


// =============================================================
// MAIN COMPONENT
// =============================================================

const AdminOrdersPage = () => {
  const [
    orders,
    setOrders,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    selectedOrder,
    setSelectedOrder,
  ] = useState(null);

  const [
    showModal,
    setShowModal,
  ] = useState(false);

  const [
    filters,
    setFilters,
  ] = useState({
    search: '',
    status: 'all',
    paymentStatus: 'all',
  });


  // ===========================================================
  // FETCH ORDERS
  // ===========================================================

  const fetchOrders = useCallback(
    async (
      isRefresh = false
    ) => {
      try {
        if (isRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError('');

        console.log(
          '📡 Loading admin orders...'
        );

        const data =
          await getAllOrdersAsAdmin();

        console.log(
          '✅ RAW ADMIN ORDERS:',
          data
        );

        const ordersArray =
          Array.isArray(data)
            ? data
            : Array.isArray(
                data?.orders
              )
            ? data.orders
            : [];

        console.log(
          '📦 ORDERS COUNT:',
          ordersArray.length
        );

        ordersArray.forEach(
          (order, index) => {
            console.log(
              `📦 ORDER ${index + 1}:`,
              {
                id: order?._id,
                status:
                  order?.status,
                paymentStatus:
                  order?.paymentStatus,
                paymentMethod:
                  order?.paymentMethod,
                paymentIntent:
                  order?.paymentIntent,
                stripeSessionId:
                  order?.stripeSessionId,
                paidAt:
                  order?.paidAt,
                total:
                  order?.total,
              }
            );
          }
        );

        setOrders(
          ordersArray
        );
      } catch (err) {
        console.error(
          '❌ Failed to load admin orders:',
          err
        );

        setError(
          err?.message ||
            'Failed to load orders.'
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );


  // ===========================================================
  // INITIAL LOAD
  // ===========================================================

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);


  // ===========================================================
  // FILTER CHANGE
  // ===========================================================

  const handleFilterChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    setFilters(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    );
  };


  // ===========================================================
  // FILTERED ORDERS
  // ===========================================================

  const filteredOrders =
    useMemo(() => {
      const search =
        filters.search
          .trim()
          .toLowerCase();

      return orders.filter(
        (order) => {
          // ---------------------------------------------------
          // SEARCH
          // ---------------------------------------------------

          if (search) {
            const orderId =
              String(
                getOrderId(order)
              ).toLowerCase();

            const customer =
              String(
                getCustomerName(
                  order
                )
              ).toLowerCase();

            const email =
              String(
                getCustomerEmail(
                  order
                )
              ).toLowerCase();

            if (
              !orderId.includes(
                search
              ) &&
              !customer.includes(
                search
              ) &&
              !email.includes(
                search
              )
            ) {
              return false;
            }
          }


          // ---------------------------------------------------
          // ORDER STATUS
          // ---------------------------------------------------

          if (
            filters.status !==
            'all'
          ) {
            const status =
              getNormalizedOrderStatus(
                order
              );

            if (
              status !==
              filters.status
            ) {
              return false;
            }
          }


          // ---------------------------------------------------
          // PAYMENT STATUS
          // ---------------------------------------------------

          if (
            filters.paymentStatus !==
            'all'
          ) {
            const paymentStatus =
              getPaymentStatus(
                order
              );

            if (
              paymentStatus !==
              filters.paymentStatus
            ) {
              return false;
            }
          }

          return true;
        }
      );
    }, [
      orders,
      filters,
    ]);


  // ===========================================================
  // SUMMARY
  // ===========================================================

  const summary = useMemo(
    () => {
      let total = 0;
      let paid = 0;
      let pending = 0;
      let completed = 0;

      orders.forEach(
        (order) => {
          total += getTotal(
            order
          );

          const paymentStatus =
            getPaymentStatus(
              order
            );

          const status =
            getNormalizedOrderStatus(
              order
            );

          if (
            paymentStatus ===
            'paid'
          ) {
            paid += 1;
          }

          if (
            paymentStatus ===
            'pending'
          ) {
            pending += 1;
          }

          if (
            status ===
            'completed'
          ) {
            completed += 1;
          }
        }
      );

      return {
        total,
        paid,
        pending,
        completed,
      };
    },
    [orders]
  );


  // ===========================================================
  // OPEN ORDER
  // ===========================================================

  const handleViewOrder = (
    order
  ) => {
    console.log(
      '👁️ SELECTED ORDER:',
      order
    );

    console.log(
      '📌 SELECTED STATUS:',
      order?.status
    );

    console.log(
      '💳 SELECTED PAYMENT:',
      order?.paymentStatus
    );

    setSelectedOrder(
      order
    );

    setShowModal(true);
  };


  // ===========================================================
  // CLOSE MODAL
  // ===========================================================

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };


  // ===========================================================
  // LOADING
  // ===========================================================

  if (loading) {
    return (
      <div className="container-fluid py-5 text-center">
        <Spinner animation="border" />
        <div className="mt-3">
          Loading orders...
        </div>
      </div>
    );
  }


  // ===========================================================
  // RENDER
  // ===========================================================

  return (
    <div className="container-fluid py-4">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="d-flex justify-content-between align-items-center mb-4">

        <div>
          <h2 className="mb-1">
            Orders
          </h2>

          <p className="text-muted mb-0">
            Manage customer orders,
            payments and order status.
          </p>
        </div>

        <Button
          variant="outline-primary"
          onClick={() =>
            fetchOrders(true)
          }
          disabled={refreshing}
        >
          {refreshing ? (
            <>
              <Spinner
                size="sm"
                animation="border"
                className="me-2"
              />
              Refreshing...
            </>
          ) : (
            <>
              🔄 Refresh
            </>
          )}
        </Button>

      </div>


      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <div className="alert alert-danger">
          <strong>
            Error:
          </strong>{' '}
          {error}
        </div>
      )}


      {/* =====================================================
          SUMMARY CARDS
      ===================================================== */}

      <Row className="g-3 mb-4">

        <Col
          xs={12}
          md={6}
          lg={3}
        >
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">
                Total Orders
              </div>

              <h3 className="mb-0">
                {orders.length}
              </h3>
            </Card.Body>
          </Card>
        </Col>


        <Col
          xs={12}
          md={6}
          lg={3}
        >
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">
                Paid Orders
              </div>

              <h3 className="mb-0 text-success">
                {summary.paid}
              </h3>
            </Card.Body>
          </Card>
        </Col>


        <Col
          xs={12}
          md={6}
          lg={3}
        >
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">
                Pending Payments
              </div>

              <h3 className="mb-0">
                {summary.pending}
              </h3>
            </Card.Body>
          </Card>
        </Col>


        <Col
          xs={12}
          md={6}
          lg={3}
        >
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">
                Completed Orders
              </div>

              <h3 className="mb-0 text-success">
                {summary.completed}
              </h3>
            </Card.Body>
          </Card>
        </Col>

      </Row>


      {/* =====================================================
          FILTERS
      ===================================================== */}

      <Card className="shadow-sm mb-4">

        <Card.Body>

          <Row className="g-3">

            <Col
              xs={12}
              md={4}
            >
              <Form.Group>
                <Form.Label>
                  Search
                </Form.Label>

                <Form.Control
                  type="text"
                  name="search"
                  placeholder="Order ID, customer or email"
                  value={
                    filters.search
                  }
                  onChange={
                    handleFilterChange
                  }
                />
              </Form.Group>
            </Col>


            <Col
              xs={12}
              md={4}
            >
              <Form.Group>
                <Form.Label>
                  Order Status
                </Form.Label>

                <Form.Select
                  name="status"
                  value={
                    filters.status
                  }
                  onChange={
                    handleFilterChange
                  }
                >
                  <option value="all">
                    All Statuses
                  </option>

                  <option value="pending">
                    Pending
                  </option>

                  <option value="processing">
                    Processing
                  </option>

                  <option value="completed">
                    Completed
                  </option>

                  <option value="shipped">
                    Shipped
                  </option>

                  <option value="delivered">
                    Delivered
                  </option>

                  <option value="cancelled">
                    Cancelled
                  </option>

                  <option value="failed">
                    Failed
                  </option>

                  <option value="refunded">
                    Refunded
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>


            <Col
              xs={12}
              md={4}
            >
              <Form.Group>
                <Form.Label>
                  Payment Status
                </Form.Label>

                <Form.Select
                  name="paymentStatus"
                  value={
                    filters.paymentStatus
                  }
                  onChange={
                    handleFilterChange
                  }
                >
                  <option value="all">
                    All Payments
                  </option>

                  <option value="paid">
                    Paid
                  </option>

                  <option value="pending">
                    Pending
                  </option>

                  <option value="unpaid">
                    Unpaid
                  </option>

                  <option value="failed">
                    Failed
                  </option>

                  <option value="refunded">
                    Refunded
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>

          </Row>

        </Card.Body>

      </Card>


      {/* =====================================================
          ORDERS TABLE
      ===================================================== */}

      <Card className="shadow-sm">

        <Card.Body className="p-0">

          <div className="table-responsive">

            <Table
              hover
              responsive
              className="mb-0 align-middle"
            >

              <thead>
                <tr>

                  <th className="px-3 py-3">
                    Order
                  </th>

                  <th className="px-3 py-3">
                    Customer
                  </th>

                  <th className="px-3 py-3">
                    Date
                  </th>

                  <th className="px-3 py-3">
                    Total
                  </th>

                  <th className="px-3 py-3">
                    Payment
                  </th>

                  <th className="px-3 py-3">
                    Order Status
                  </th>

                  <th className="px-3 py-3 text-end">
                    Action
                  </th>

                </tr>
              </thead>


              <tbody>

                {filteredOrders.length ===
                0 ? (

                  <tr>
                    <td
                      colSpan="7"
                      className="text-center py-5 text-muted"
                    >
                      No orders found.
                    </td>
                  </tr>

                ) : (

                  filteredOrders.map(
                    (order) => {

                      const orderStatus =
                        getNormalizedOrderStatus(
                          order
                        );

                      const paymentStatus =
                        getPaymentStatus(
                          order
                        );

                      return (
                        <tr
                          key={
                            getOrderId(
                              order
                            )
                          }
                        >

                          {/* ORDER ID */}

                          <td className="px-3 py-3">

                            <div className="fw-semibold">
                              #
                              {String(
                                getOrderId(
                                  order
                                )
                              ).slice(
                                -8
                              )}
                            </div>

                            <small className="text-muted">
                              {getOrderId(
                                order
                              )}
                            </small>

                          </td>


                          {/* CUSTOMER */}

                          <td className="px-3 py-3">

                            <div className="fw-semibold">
                              {getCustomerName(
                                order
                              )}
                            </div>

                            <small className="text-muted">
                              {getCustomerEmail(
                                order
                              )}
                            </small>

                          </td>


                          {/* DATE */}

                          <td className="px-3 py-3">
                            {formatDate(
                              order?.createdAt
                            )}
                          </td>


                          {/* TOTAL */}

                          <td className="px-3 py-3 fw-semibold">
                            {formatMoney(
                              getTotal(
                                order
                              )
                            )}
                          </td>


                          {/* PAYMENT */}

                          <td className="px-3 py-3">

                            <div>
                              {getPaymentBadge(
                                order
                              )}
                            </div>

                            {paymentStatus ===
                              'paid' &&
                              order?.paymentMethod && (
                                <small className="text-muted d-block mt-1">
                                  {getPaymentMethod(
                                    order
                                  )}
                                </small>
                              )}

                          </td>


                          {/* ORDER STATUS */}

                          <td className="px-3 py-3">

                            {getStatusBadge(
                              orderStatus
                            )}

                            <small className="text-muted d-block mt-1">
                              {orderStatus ||
                                'No status'}
                            </small>

                          </td>


                          {/* ACTION */}

                          <td className="px-3 py-3 text-end">

                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleViewOrder(
                                  order
                                )
                              }
                            >
                              View
                            </Button>

                          </td>

                        </tr>
                      );
                    }
                  )

                )}

              </tbody>

            </Table>

          </div>

        </Card.Body>

      </Card>


      {/* =====================================================
          ORDER DETAILS MODAL
      ===================================================== */}

      <Modal
        show={showModal}
        onHide={
          handleCloseModal
        }
        size="xl"
        centered
      >

        <Modal.Header closeButton>

          <Modal.Title>
            Order #
            {String(
              getOrderId(
                selectedOrder
              )
            ).slice(-8)}
          </Modal.Title>

        </Modal.Header>


        <Modal.Body>

          {selectedOrder && (

            <>

              {/* =================================================
                  ORDER SUMMARY
              ================================================= */}

              <Card className="mb-4">

                <Card.Header>
                  <strong>
                    Order Summary
                  </strong>
                </Card.Header>

                <Card.Body>

                  <Row className="g-3">

                    <Col
                      xs={12}
                      md={3}
                    >
                      <div className="text-muted small">
                        Order Status
                      </div>

                      <div className="mt-1">
                        {getStatusBadge(
                          selectedOrder?.status
                        )}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={3}
                    >
                      <div className="text-muted small">
                        Payment Status
                      </div>

                      <div className="mt-1">
                        {getPaymentBadge(
                          selectedOrder
                        )}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={3}
                    >
                      <div className="text-muted small">
                        Payment Method
                      </div>

                      <div className="fw-semibold mt-1">
                        {getPaymentMethod(
                          selectedOrder
                        )}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={3}
                    >
                      <div className="text-muted small">
                        Order Date
                      </div>

                      <div className="fw-semibold mt-1">
                        {formatDate(
                          selectedOrder?.createdAt
                        )}
                      </div>
                    </Col>

                  </Row>

                </Card.Body>

              </Card>


              {/* =================================================
                  CUSTOMER
              ================================================= */}

              <Card className="mb-4">

                <Card.Header>
                  <strong>
                    Customer
                  </strong>
                </Card.Header>

                <Card.Body>

                  <Row>

                    <Col
                      md={6}
                      className="mb-3"
                    >
                      <div className="text-muted small">
                        Name
                      </div>

                      <div className="fw-semibold">
                        {getCustomerName(
                          selectedOrder
                        )}
                      </div>
                    </Col>


                    <Col
                      md={6}
                      className="mb-3"
                    >
                      <div className="text-muted small">
                        Email
                      </div>

                      <div className="fw-semibold">
                        {getCustomerEmail(
                          selectedOrder
                        ) ||
                          '—'}
                      </div>
                    </Col>

                  </Row>

                </Card.Body>

              </Card>


              {/* =================================================
                  SHIPPING ADDRESS
              ================================================= */}

              {getShippingAddress(
                selectedOrder
              ) && (

                <Card className="mb-4">

                  <Card.Header>
                    <strong>
                      Shipping Address
                    </strong>
                  </Card.Header>

                  <Card.Body>

                    {(() => {
                      const address =
                        getShippingAddress(
                          selectedOrder
                        );

                      return (
                        <div>

                          <div>
                            {address?.address ||
                              ''}
                          </div>

                          <div>
                            {address?.city ||
                              ''}
                          </div>

                          <div>
                            {address?.postcode ||
                              ''}
                          </div>

                          <div>
                            {address?.country ||
                              ''}
                          </div>

                        </div>
                      );
                    })()}

                  </Card.Body>

                </Card>
              )}


              {/* =================================================
                  PRODUCTS
              ================================================= */}

              <Card className="mb-4">

                <Card.Header>
                  <strong>
                    Products
                  </strong>
                </Card.Header>

                <Card.Body className="p-0">

                  <div className="table-responsive">

                    <Table
                      hover
                      className="mb-0"
                    >

                      <thead>

                        <tr>

                          <th>
                            Product
                          </th>

                          <th>
                            Price
                          </th>

                          <th>
                            Quantity
                          </th>

                          <th className="text-end">
                            Total
                          </th>

                        </tr>

                      </thead>


                      <tbody>

                        {getOrderProducts(
                          selectedOrder
                        ).map(
                          (
                            product,
                            index
                          ) => {

                            const price =
                              getProductPrice(
                                product
                              );

                            const quantity =
                              getProductQuantity(
                                product
                              );

                            return (
                              <tr
                                key={
                                  product?._id ||
                                  product?.id ||
                                  index
                                }
                              >

                                <td>
                                  {getProductName(
                                    product
                                  )}
                                </td>

                                <td>
                                  {formatMoney(
                                    price
                                  )}
                                </td>

                                <td>
                                  {quantity}
                                </td>

                                <td className="text-end fw-semibold">
                                  {formatMoney(
                                    price *
                                      quantity
                                  )}
                                </td>

                              </tr>
                            );
                          }
                        )}

                      </tbody>

                    </Table>

                  </div>

                </Card.Body>

              </Card>


              {/* =================================================
                  PAYMENT SUMMARY
              ================================================= */}

              <Card className="mb-4">

                <Card.Header>
                  <strong>
                    Payment Summary
                  </strong>
                </Card.Header>

                <Card.Body>

                  <div className="d-flex justify-content-between mb-2">
                    <span>
                      Subtotal
                    </span>

                    <strong>
                      {formatMoney(
                        getSubtotal(
                          selectedOrder
                        )
                      )}
                    </strong>
                  </div>


                  <div className="d-flex justify-content-between mb-2">
                    <span>
                      Delivery
                    </span>

                    <strong>
                      {formatMoney(
                        getShipping(
                          selectedOrder
                        )
                      )}
                    </strong>
                  </div>


                  <div className="d-flex justify-content-between mb-2">
                    <span>
                      Tax
                    </span>

                    <strong>
                      {formatMoney(
                        getTax(
                          selectedOrder
                        )
                      )}
                    </strong>
                  </div>


                  <hr />


                  <div className="d-flex justify-content-between">

                    <span className="fw-bold">
                      Total
                    </span>

                    <span className="fw-bold fs-5">
                      {formatMoney(
                        getTotal(
                          selectedOrder
                        )
                      )}
                    </span>

                  </div>

                </Card.Body>

              </Card>


              {/* =================================================
                  STRIPE INFORMATION
              ================================================= */}

              <Card className="mb-4">

                <Card.Header>
                  <strong>
                    Stripe Information
                  </strong>
                </Card.Header>

                <Card.Body>

                  <Row className="g-3">

                    <Col
                      xs={12}
                      md={6}
                    >
                      <div className="text-muted small">
                        Payment Intent
                      </div>

                      <div
                        className="small text-break"
                      >
                        {selectedOrder?.paymentIntent ||
                          '—'}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={6}
                    >
                      <div className="text-muted small">
                        Stripe Session
                      </div>

                      <div
                        className="small text-break"
                      >
                        {selectedOrder?.stripeSessionId ||
                          '—'}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={6}
                    >
                      <div className="text-muted small">
                        Paid At
                      </div>

                      <div className="small">
                        {formatDate(
                          selectedOrder?.paidAt
                        )}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={6}
                    >
                      <div className="text-muted small">
                        Payment Error
                      </div>

                      <div className="small text-danger">
                        {selectedOrder?.paymentError ||
                          'None'}
                      </div>
                    </Col>

                  </Row>

                </Card.Body>

              </Card>


              {/* =================================================
                  DEBUG INFORMATION
              ================================================= */}

              <Card className="border-warning">

                <Card.Header className="bg-warning-subtle">
                  <strong>
                    Payment / Order Debug
                  </strong>
                </Card.Header>

                <Card.Body>

                  <Row className="g-3">

                    <Col
                      xs={12}
                      md={6}
                    >
                      <strong>
                        MongoDB paymentStatus:
                      </strong>

                      <div className="mt-1">
                        {selectedOrder?.paymentStatus ??
                          'undefined'}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={6}
                    >
                      <strong>
                        MongoDB status:
                      </strong>

                      <div className="mt-1">
                        {selectedOrder?.status ??
                          'undefined'}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={6}
                    >
                      <strong>
                        paymentMethod:
                      </strong>

                      <div className="mt-1">
                        {selectedOrder?.paymentMethod ??
                          'undefined'}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={6}
                    >
                      <strong>
                        paymentIntent:
                      </strong>

                      <div className="mt-1 text-break">
                        {selectedOrder?.paymentIntent ??
                          'undefined'}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={6}
                    >
                      <strong>
                        stripeSessionId:
                      </strong>

                      <div className="mt-1 text-break">
                        {selectedOrder?.stripeSessionId ??
                          'undefined'}
                      </div>
                    </Col>


                    <Col
                      xs={12}
                      md={6}
                    >
                      <strong>
                        paidAt:
                      </strong>

                      <div className="mt-1">
                        {selectedOrder?.paidAt
                          ? formatDate(
                              selectedOrder.paidAt
                            )
                          : 'undefined'}
                      </div>
                    </Col>

                  </Row>

                </Card.Body>

              </Card>

            </>
          )}

        </Modal.Body>


        <Modal.Footer>

          <Button
            variant="secondary"
            onClick={
              handleCloseModal
            }
          >
            Close
          </Button>

        </Modal.Footer>

      </Modal>

    </div>
  );
};


export default AdminOrdersPage;